/*
 * Zaunkonfigurator 3D – Berechnungskern (UI-frei, in Node und Browser nutzbar).
 * Fork des Zaunplaner-Kerns, erweitert um Zaunverlauf aus mehreren Abschnitten,
 * Gefälle (gestufte Felder) und die Lieferart "Bausatz" (Einzelteile).
 *
 * Zentrale Funktion:
 *   berechneStueckliste(eingaben, daten) -> { ok, positionen[], hinweise[], annahmen[], ... }
 *
 * eingaben: { modellId, hoehe_cm, befestigungId, mitKappen,
 *             laenge_m?,                                     // ohne segmente_cm: 1 gerader Abschnitt
 *             segmente_cm?: [{ laenge_cm, hoehen_delta_cm }],// Zaunverlauf aus dem 3D-Planer
 *             lieferartId?,                                  // 'vormontiert' (Default) | 'bausatz'
 *             feldmodusId?, mass_max_cm?,                    // 'standard' (Default) | 'mass' (Felder nach Maß)
 *             pfostensystemId?,                              // 'ipf' (Default, bis 120 cm) | 'halter' (regulärer Pfosten + Halter)
 *             entfernteTypen? }                              // abgewählte Zubehör-Positionen, z. B. ['winkel']
 * daten:    Inhalt von konfigurator-data.json (Regeln + DB-Export)
 *
 * Gefälle-Annahme: Vormontierte Felder bleiben waagerecht und werden dem Hang
 * folgend gestuft; die Feldaufteilung rechnet daher mit der horizontalen Länge
 * jedes Abschnitts. Felder laufen nicht um Ecken – an jedem Eckpunkt sitzt ein
 * Pfosten, Eckpfosten werden von beiden Abschnitten gemeinsam genutzt.
 */
(function (root, factory) {
  if (typeof module !== 'undefined' && module.exports) { module.exports = factory(require('./feld-logic.js')); }
  else { root.ZaunLogic = factory(root.FeldLogic); }
}(typeof self !== 'undefined' ? self : this, function (FeldLogic) {
  'use strict';

  function rund2(x) { return Math.round(x * 100) / 100; }

  function meterText(cm) {
    return (cm / 100).toFixed(2).replace('.', ',') + ' m';
  }

  /*
   * Sucht die Kombination von Feldlängen, die die Wunschlänge mit minimalem
   * Überstand abdeckt. Rangfolge: möglichst keine Felder über langgutAbCm
   * (Speditions-Langgutzuschlag), dann minimaler Überstand, dann weniger Felder.
   */
  function feldAufteilung(laengeCm, feldlaengenCm, langgutAbCm) {
    var laengen = feldlaengenCm.slice().sort(function (a, b) { return b - a; });
    var max = laengen[0];
    var min = laengen[laengen.length - 1];
    var nMin = Math.max(1, Math.ceil(laengeCm / max));
    var nMax = Math.max(nMin, Math.ceil(laengeCm / min));
    var best = null;

    function istBesser(a, b) {
      if (a.langgutAnzahl !== b.langgutAnzahl) return a.langgutAnzahl < b.langgutAnzahl;
      if (a.ueberstandCm !== b.ueberstandCm) return a.ueberstandCm < b.ueberstandCm;
      return a.anzahl < b.anzahl;
    }

    for (var n = nMin; n <= nMax; n++) {
      (function kombiniere(idx, rest, counts, summe) {
        if (idx === laengen.length - 1) {
          counts[idx] = rest;
          summe += rest * laengen[idx];
          if (summe < laengeCm) return;
          var langgut = 0;
          if (langgutAbCm) {
            for (var j = 0; j < laengen.length; j++) {
              if (laengen[j] > langgutAbCm) langgut += counts[j];
            }
          }
          var kandidat = { counts: counts.slice(), anzahl: n, gesamtCm: summe, ueberstandCm: summe - laengeCm, langgutAnzahl: langgut };
          if (!best || istBesser(kandidat, best)) best = kandidat;
          return;
        }
        for (var k = rest; k >= 0; k--) {
          counts[idx] = k;
          kombiniere(idx + 1, rest - k, counts, summe + k * laengen[idx]);
        }
      }(0, n, [], 0));
      if (best && best.ueberstandCm === 0 && best.langgutAnzahl === 0) break; // perfekt bei minimaler Feldzahl
    }
    if (!best) return null;

    var felder = [];
    for (var i = 0; i < laengen.length; i++) {
      if (best.counts[i] > 0) felder.push({ laengeCm: laengen[i], anzahl: best.counts[i] });
    }
    return { felder: felder, anzahl: best.anzahl, gesamtCm: best.gesamtCm, ueberstandCm: best.ueberstandCm, langgutAnzahl: best.langgutAnzahl };
  }

  function findeModell(daten, id) {
    for (var i = 0; i < daten.modelle.length; i++) {
      if (daten.modelle[i].id === id) return daten.modelle[i];
    }
    return null;
  }

  function findeBefestigung(daten, id) {
    for (var i = 0; i < daten.befestigungen.length; i++) {
      if (daten.befestigungen[i].id === id) return daten.befestigungen[i];
    }
    return null;
  }

  function findeLieferart(daten, id) {
    var liste = daten.lieferarten || [];
    for (var i = 0; i < liste.length; i++) {
      if (liste[i].id === id) return liste[i];
    }
    return liste[0] || { id: 'vormontiert', name: 'Fertig montierte Felder' };
  }

  /* Normalisiert die Eingabe auf eine Abschnittsliste (Rückwärtskompatibel zu laenge_m). */
  function segmenteAusEingaben(eingaben) {
    if (eingaben.segmente_cm && eingaben.segmente_cm.length) {
      return eingaben.segmente_cm.map(function (s) {
        return { laenge_cm: Math.round(Number(s.laenge_cm) || 0), hoehen_delta_cm: Number(s.hoehen_delta_cm) || 0 };
      });
    }
    var laengeM = parseFloat(eingaben.laenge_m);
    if (isNaN(laengeM) || laengeM <= 0) return [];
    return [{ laenge_cm: Math.round(laengeM * 100), hoehen_delta_cm: 0 }];
  }

  /* Feldaufteilung je Abschnitt + zusammengeführte Gesamtsicht. */
  function feldAufteilungSegmente(segmente, feldlaengenCm, langgutAbCm) {
    var gesamt = { felder: [], anzahl: 0, gesamtCm: 0, ueberstandCm: 0, langgutAnzahl: 0, proSegment: [] };
    for (var i = 0; i < segmente.length; i++) {
      var a = feldAufteilung(segmente[i].laenge_cm, feldlaengenCm, langgutAbCm);
      if (!a) return null;
      gesamt.proSegment.push(a);
      gesamt.anzahl += a.anzahl;
      gesamt.gesamtCm += a.gesamtCm;
      gesamt.ueberstandCm += a.ueberstandCm;
      gesamt.langgutAnzahl += a.langgutAnzahl || 0;
      a.felder.forEach(function (g) {
        var vorhanden = gesamt.felder.filter(function (x) { return x.laengeCm === g.laengeCm; })[0];
        if (vorhanden) vorhanden.anzahl += g.anzahl;
        else gesamt.felder.push({ laengeCm: g.laengeCm, anzahl: g.anzahl });
      });
    }
    gesamt.felder.sort(function (a, b) { return b.laengeCm - a.laengeCm; });
    return gesamt;
  }

  /*
   * Felder nach Maß: Jeder Abschnitt wird in gleich lange Felder geteilt
   * (maximal maxCm) – ohne Überstand; Rundungs-Reste verteilen sich als
   * +1-cm-Felder. Gleiche Struktur wie feldAufteilungSegmente.
   */
  function feldAufteilungNachMass(segmente, maxCm) {
    var gesamt = { felder: [], anzahl: 0, gesamtCm: 0, ueberstandCm: 0, langgutAnzahl: 0, proSegment: [] };
    for (var i = 0; i < segmente.length; i++) {
      var len = segmente[i].laenge_cm;
      var n = Math.max(1, Math.ceil(len / maxCm));
      var basis = Math.floor(len / n);
      var rest = len - basis * n; // so viele Felder bekommen 1 cm mehr
      var a = { felder: [], anzahl: n, gesamtCm: len, ueberstandCm: 0, langgutAnzahl: 0 };
      if (n - rest > 0) a.felder.push({ laengeCm: basis, anzahl: n - rest });
      if (rest > 0) a.felder.push({ laengeCm: basis + 1, anzahl: rest });
      a.felder.sort(function (x, y) { return y.laengeCm - x.laengeCm; });
      gesamt.proSegment.push(a);
      gesamt.anzahl += n;
      gesamt.gesamtCm += len;
      a.felder.forEach(function (g) {
        var vorhanden = gesamt.felder.filter(function (x) { return x.laengeCm === g.laengeCm; })[0];
        if (vorhanden) vorhanden.anzahl += g.anzahl;
        else gesamt.felder.push({ laengeCm: g.laengeCm, anzahl: g.anzahl });
      });
    }
    gesamt.felder.sort(function (a, b) { return b.laengeCm - a.laengeCm; });
    return gesamt;
  }

  /*
   * Zusätzliche Pfostenlänge (cm) je Pfosten durch Hang-Stufen bei gestufter
   * Montage: Ein Pfosten muss vom Boden an seiner Position bis zur Oberkante
   * des höheren Nachbarfelds reichen (gleiche Geometrie wie die 3D-Ansicht).
   * Rückgabe: ein Eintrag je Pfosten in Verlaufsreihenfolge; geteilte
   * Eckpfosten (Segmentwechsel, Ring) zählen einmal mit dem Maximum.
   */
  function pfostenExtras(segmente, aufteilung, geschlossen) {
    var proSegment = [];
    for (var i = 0; i < segmente.length; i++) {
      var L = segmente[i].laenge_cm;
      var delta = segmente[i].hoehen_delta_cm || 0;
      var t = function (s) { return L ? delta * s / L : 0; };

      var felder = [];
      ((aufteilung.proSegment[i] || {}).felder || []).forEach(function (g) {
        for (var k = 0; k < g.anzahl; k++) felder.push(g.laengeCm);
      });
      if (!felder.length) felder = [L];
      var summe = felder.reduce(function (a, b) { return a + b; }, 0);
      if (summe > L) felder[felder.length - 1] = Math.max(1, felder[felder.length - 1] - (summe - L));

      // Feld-Unterkanten (waagerecht am höheren Ende) und Pfosten-Extras
      var basen = [];
      var s = 0;
      felder.forEach(function (f) {
        basen.push(Math.max(t(s), t(s + f)));
        s += f;
      });
      var extras = [];
      var pos = 0;
      for (var k = 0; k <= felder.length; k++) {
        var nachbarn = [];
        if (k > 0) nachbarn.push(basen[k - 1]);
        if (k < felder.length) nachbarn.push(basen[k]);
        extras.push(Math.max(0, Math.max.apply(null, nachbarn) - t(pos)));
        if (k < felder.length) pos += felder[k];
      }
      proSegment.push(extras);
    }

    // Zusammenführen: Eckpfosten gehören zu beiden Abschnitten -> Maximum
    var alle = proSegment[0] ? proSegment[0].slice() : [];
    for (var j = 1; j < proSegment.length; j++) {
      var seg = proSegment[j];
      alle[alle.length - 1] = Math.max(alle[alle.length - 1], seg[0]);
      alle = alle.concat(seg.slice(1));
    }
    if (geschlossen && alle.length > 1) {
      alle[0] = Math.max(alle[0], alle[alle.length - 1]);
      alle.pop();
    }
    return alle;
  }

  /*
   * Bausatz-Preis eines Feldes: Einzelteile (Latten + Riegel + Schrauben,
   * ohne Montagepauschale) + Aufschlag, kaufmännisch aufs Preisraster
   * gerundet (Aufrunden würde den Montage-Vorteil je nach Rasterlage
   * teilweise auffressen). Die Formel muss linear in den Teilpreisen
   * bleiben: Die öffentliche Datenvariante (Aufschlag bereits in die
   * Teilpreise eingerechnet, aufschlag_pct = 0) soll dasselbe Ergebnis
   * liefern wie die interne EK-Variante.
   */
  function bausatzPreis(kalk, p) {
    return FeldLogic.berechneBausatzPreis(kalk, p);
  }

  function konfiguratorFeldDaten(modell, hoeheCm, laengeCm, lieferart, kalk, version) {
    return {
      typ: 'zaunfeld',
      modellId: modell.id,
      modellDb: modell.modell_db,
      hoeheCm: hoeheCm,
      laengeCm: laengeCm,
      lieferart: lieferart,
      regelversion: version || 1,
      lattenSku: kalk.latten.sku,
      lattenAnzahl: kalk.latten.anzahl,
      riegelSku: kalk.riegel.sku,
      riegelAnzahl: kalk.riegel.anzahl,
      schraubenAnzahl: kalk.schrauben.anzahl,
      montage: lieferart !== 'bausatz'
    };
  }

  /* Datenpaket für FeldLogic (Maßfeld-Kalkulation): gleiche Daten, aber Feld- statt Zaun-Grenzen. */
  function feldKalkDaten(daten, massfeld) {
    var d = {};
    for (var k in daten) {
      if (Object.prototype.hasOwnProperty.call(daten, k)) d[k] = daten[k];
    }
    d.grenzen = {
      min_laenge_m: (massfeld.min_laenge_cm || 50) / 100,
      max_laenge_m: (massfeld.max_laenge_cm || 300) / 100
    };
    return d;
  }

  function optionenListe(eintrag) {
    var o = [];
    if (eintrag.option1 != null && eintrag.option1 !== '') o.push(String(eintrag.option1));
    if (eintrag.option2 != null && eintrag.option2 !== '') o.push(String(eintrag.option2));
    if (eintrag.option3 != null && eintrag.option3 !== '') o.push(String(eintrag.option3));
    return o;
  }

  function berechneStueckliste(eingaben, daten) {
    var fehler = [];
    var grenzen = daten.grenzen || { min_laenge_m: 1, max_laenge_m: 200 };

    var segmente = segmenteAusEingaben(eingaben);
    var laengeCm = 0;
    segmente.forEach(function (s) { laengeCm += s.laenge_cm; });
    var laengeM = laengeCm / 100;

    if (!segmente.length || laengeCm <= 0) {
      fehler.push('Bitte einen Zaunverlauf planen (mindestens zwei Punkte).');
    } else if (laengeM < grenzen.min_laenge_m || laengeM > grenzen.max_laenge_m) {
      fehler.push('Die Zaunlänge muss zwischen ' + grenzen.min_laenge_m + ' m und ' + grenzen.max_laenge_m + ' m liegen (aktuell ' + meterText(laengeCm) + ').');
    }
    segmente.forEach(function (s, i) {
      if (s.laenge_cm > 0 && s.laenge_cm < 100) {
        fehler.push('Abschnitt ' + (i + 1) + ' ist kürzer als 1 m – bitte Punkte zusammenlegen.');
      }
    });

    // Gefälle je Abschnitt (in %); Felder werden waagerecht gestuft montiert.
    var maxGefaellePct = 0;
    segmente.forEach(function (s) {
      if (s.laenge_cm > 0) {
        var pct = Math.abs(s.hoehen_delta_cm) / s.laenge_cm * 100;
        if (pct > maxGefaellePct) maxGefaellePct = pct;
      }
    });
    var maxGefaelleErlaubt = grenzen.max_gefaelle_pct || 35;
    if (maxGefaellePct > maxGefaelleErlaubt) {
      fehler.push('Ein Abschnitt hat mehr als ' + maxGefaelleErlaubt + ' % Gefälle (' + Math.round(maxGefaellePct) +
        ' %) – so steiles Gelände planen wir lieber gemeinsam: Ruf uns an oder sende die Konfiguration als Anfrage.');
    }

    var modell = findeModell(daten, eingaben.modellId);
    if (!modell) fehler.push('Bitte ein Zaunmodell wählen.');

    var hoehe = parseInt(eingaben.hoehe_cm, 10);
    if (!hoehe) fehler.push('Bitte eine Zaunhöhe wählen.');

    var befestigung = findeBefestigung(daten, eingaben.befestigungId);
    if (!befestigung) fehler.push('Bitte eine Befestigungsart wählen.');

    var lieferart = findeLieferart(daten, eingaben.lieferartId || 'vormontiert');

    if (fehler.length) return { ok: false, fehler: fehler };

    var hinweise = [];
    var positionen = [];

    // Abgewählte Zubehör-Positionen (Kunde besorgt sie z. B. selbst im Baumarkt)
    var entfernteTypen = eingaben.entfernteTypen || [];
    function istEntfernt(typ) { return entfernteTypen.indexOf(typ) >= 0; }

    // --- Zaunfelder ---
    var feldArtikel = daten.felder.filter(function (f) {
      return f.modell === modell.modell_db &&
             f.ausfuehrung === modell.ausfuehrung &&
             f.hoehe_cm === hoehe;
    });
    var hoeheStandard = feldArtikel.length > 0;

    // Feldmodus: 'standard' (Lager-Längen) oder 'mass' (Felder nach Maß,
    // gleich lange Felder je Abschnitt, kalkuliert wie im Zaunfeld-Konfigurator).
    // Höhen ohne Lager-Feld erzwingen den Maß-Modus.
    var massfeld = daten.massfeld;
    var massVerfuegbar = !!(massfeld && FeldLogic && daten.parameter && daten.latten_artikel &&
      daten.riegel_artikel && modell.riegel_name);
    if (!hoeheStandard && !massVerfuegbar) {
      return { ok: false, fehler: ['Das Modell "' + modell.name + '" ist in ' + hoehe + ' cm Höhe nicht verfügbar.'] };
    }
    var massModus = massVerfuegbar && (eingaben.feldmodusId === 'mass' || !hoeheStandard);
    if (eingaben.feldmodusId === 'mass' && !massVerfuegbar) {
      hinweise.push('Felder nach Maß sind derzeit nicht verfügbar – es werden Standard-Feldlängen verwendet.');
    }
    if (!hoeheStandard && massModus && eingaben.feldmodusId !== 'mass') {
      hinweise.push('Zaunfelder in ' + hoehe + ' cm Höhe gibt es nicht ab Lager – sie werden für dich nach Maß gefertigt.');
    }
    var langgutAb = grenzen.langgut_ab_cm || null;
    var massMaxCm = null;
    var feldDaten = null;
    var aufteilung;

    if (massModus) {
      feldDaten = feldKalkDaten(daten, massfeld);
      massMaxCm = parseInt(eingaben.mass_max_cm, 10) || massfeld.standard_max_cm || 240;
      massMaxCm = Math.max(massfeld.min_laenge_cm || 50, Math.min(massfeld.max_laenge_cm || 300, massMaxCm));
      var modellMaxCm = Math.floor(FeldLogic.maxLaengeFuerModell(feldDaten, modell.id) * 100);
      if (modellMaxCm > 0 && massMaxCm > modellMaxCm) {
        massMaxCm = modellMaxCm;
        hinweise.push('Für ' + modell.name + ' sind maximal ' + modellMaxCm + ' cm Feldlänge möglich (Riegellängen).');
      }
      aufteilung = feldAufteilungNachMass(segmente, massMaxCm);
    } else {
      aufteilung = feldAufteilungSegmente(segmente, feldArtikel.map(function (f) { return f.laenge_cm; }), langgutAb);
    }
    if (!aufteilung) {
      return { ok: false, fehler: ['Für diese Länge konnte keine Feldaufteilung gefunden werden.'] };
    }

    // Lieferart "Bausatz": gleiche Felder, aber unmontiert. Der Preis wird aus
    // den Einzelteilen kalkuliert (bausatzPreis; Entscheidung 26.07.2026,
    // ersetzt die frühere Annäherung "Feldpreis − 10 %"). Abgerechnet wird
    // über das Preisraster-Produkt, weil es im Shop keine Bausatz-Varianten
    // der Felder gibt.
    var bausatz = lieferart.id === 'bausatz';
    if (bausatz && !massVerfuegbar) {
      // Ohne Teile-Kalkulation (FeldLogic, Latten-/Riegel-Artikel oder
      // Raster-Produkt fehlen) gibt es keinen belastbaren Bausatz-Preis.
      bausatz = false;
      lieferart = findeLieferart(daten, 'vormontiert');
      hinweise.push('Die Bausatz-Kalkulation ist derzeit nicht verfügbar – Stückliste und Preise gelten für fertig montierte Felder.');
    }
    if (bausatz && !feldDaten) feldDaten = feldKalkDaten(daten, massfeld);
    var bausatzKalk = []; // je Feldlänge die Teile-Kalkulation (für Preis + Teile-Hinweis)

    if (massModus) {
      var langgutDrin = false;
      var massFehler = null;
      aufteilung.felder.forEach(function (gruppe) {
        if (massFehler) return;
        var kalk = FeldLogic.berechneFeld({ modellId: modell.id, laenge_m: gruppe.laengeCm / 100, hoehe_cm: hoehe }, feldDaten);
        if (!kalk.ok) { massFehler = kalk.fehler.join(' '); return; }
        if (kalk.langgut) langgutDrin = true;
        // Bausatz: Teile-Kalkulation statt Preis des montierten Feldes.
        var einzel = kalk.shopPreis;
        if (bausatz) {
          einzel = bausatzPreis(kalk, daten.parameter);
          bausatzKalk.push({ laengeCm: gruppe.laengeCm, kalk: kalk });
        }
        positionen.push({
          typ: 'massfeld',
          sku: null,
          bezeichnung: 'Zaunfeld nach Maß ' + modell.name + ', ' + hoehe + ' × ' + gruppe.laengeCm + ' cm' +
            (bausatz ? ' – Bausatz (unmontiert, individuell zugeschnitten)' : ' (individuell gefertigt)'),
          handle: kalk.langgut ? (massfeld.handle_langgut || massfeld.handle) : massfeld.handle,
          optionen: [],
          preisVariante: true, // Adapter: Variante des Preisraster-Produkts nach Preis wählen
          massfeld: { modell: modell.name, hoehe_cm: hoehe, laenge_cm: gruppe.laengeCm },
          konfiguratorFeld: konfiguratorFeldDaten(modell, hoehe, gruppe.laengeCm, lieferart.id, kalk, daten.version),
          lieferart: lieferart.id,
          menge: gruppe.anzahl,
          einzelpreis: einzel
        });
      });
      if (massFehler) return { ok: false, fehler: [massFehler] };
      hinweise.push(bausatz
        ? 'Felder nach Maß als Bausatz: Latten und Riegel kommen passgenau zugeschnitten, aber unmontiert – ' +
          'der Preis ist direkt aus den Einzelteilen kalkuliert.'
        : 'Felder nach Maß: Alle Felder eines Abschnitts sind gleich lang und werden passgenau gefertigt – kein Kürzen vor Ort nötig.');
      if (langgutDrin) {
        hinweise.push('Feldlängen über ' + ((daten.versand && daten.versand.langgut_ab_cm) || 240) +
          ' cm versendet die Spedition als Langgut – der Zuschlag steckt im Versandpreis an der Kasse.');
      }
    } else {
      var standardFehler = null;
      aufteilung.felder.forEach(function (gruppe) {
        if (standardFehler) return;
        var artikel = feldArtikel.filter(function (f) { return f.laenge_cm === gruppe.laengeCm; })[0];
        var pos = {
          typ: 'feld',
          sku: artikel.sku,
          bezeichnung: 'Zaunfeld ' + modell.name + ', ' + hoehe + ' × ' + gruppe.laengeCm + ' cm' +
            (bausatz ? ' – Bausatz (unmontiert)' : ''),
          handle: artikel.handle,
          optionen: optionenListe(artikel),
          lieferart: lieferart.id,
          menge: gruppe.anzahl,
          einzelpreis: artikel.preis != null ? Number(artikel.preis) : null
        };
        if (bausatz) {
          // Preis aus den Einzelteilen des Feldes (gleiche Kalkulation wie
          // Felder nach Maß, nur ohne Montagepauschale). Abgerechnet wird über
          // die passende Preis-Variante des Preisraster-Produkts; die
          // Kommissionierung erkennt das Feld an der Bausatz-Eigenschaft.
          var kalk = FeldLogic.berechneFeld(
            { modellId: modell.id, laenge_m: gruppe.laengeCm / 100, hoehe_cm: hoehe }, feldDaten);
          if (!kalk.ok) { standardFehler = kalk.fehler.join(' '); return; }
          pos.handle = gruppe.laengeCm > ((daten.versand && daten.versand.langgut_ab_cm) || 240)
            ? (massfeld.handle_langgut || massfeld.handle)
            : massfeld.handle;
          pos.optionen = [];
          pos.preisVariante = true; // Adapter: Variante des Preisraster-Produkts nach Preis wählen
          pos.bausatzFeld = { modell: modell.name, hoehe_cm: hoehe, laenge_cm: gruppe.laengeCm };
          pos.konfiguratorFeld = konfiguratorFeldDaten(modell, hoehe, gruppe.laengeCm, lieferart.id, kalk, daten.version);
          pos.einzelpreis = bausatzPreis(kalk, daten.parameter);
          bausatzKalk.push({ laengeCm: gruppe.laengeCm, kalk: kalk });
        }
        positionen.push(pos);
      });
      if (standardFehler) return { ok: false, fehler: [standardFehler] };
      if (langgutAb && aufteilung.langgutAnzahl > 0) {
        hinweise.push('Für diese Länge ließen sich Feldlängen über ' + langgutAb +
          ' cm nicht vermeiden – die Spedition berechnet dafür einen Langgut-Zuschlag.');
      }
    }

    if (bausatz && bausatzKalk.length) {
      var teileText = bausatzKalk.map(function (bk) {
        return 'Feld ' + bk.laengeCm + ' cm: ' + bk.kalk.latten.anzahl + ' Latten + ' +
          bk.kalk.riegel.anzahl + ' Riegel à ' + bk.kalk.riegel.laenge_cm + ' cm';
      }).join('; ');
      hinweise.push('Bausatz-Lieferung: Latten, Riegel und Schrauben kommen lose (' + teileText +
        '). Der Preis ist direkt aus den Einzelteilen kalkuliert – die Montage entfällt.');
    }

    // --- Pfosten ---
    // Bei befestigung.ohne_pfosten (z. B. "Pfosten existieren bereits") werden
    // Pfosten und Kappen nicht mitbestellt; pfostenAnzahl bleibt als Info erhalten.
    // Geschlossener Verlauf (Ring): Start- und Endpfosten sind derselbe -> ein Pfosten weniger.
    var ohnePfosten = !!befestigung.ohne_pfosten;
    var pfostenAnzahl = aufteilung.anzahl + (eingaben.geschlossen ? 0 : 1);
    var pfostenRegel = null;

    var pfostenZuLang = 0;
    if (!ohnePfosten) {
      // Pfostensystem: 'ipf' (Nut + T-Eisen/Dübelplatte, bis 120 cm),
      // 'teisen' (T-Eisenpfosten aus Stahl, 14B/14C) oder
      // 'halter' (regulärer Vierkantpfosten ohne Nut im Pfostenhalter, längere Längen).
      var systemWunsch = ['halter', 'teisen'].indexOf(eingaben.pfostensystemId) >= 0 ? eingaben.pfostensystemId : 'ipf';
      var findePfostenRegel = function (system) {
        for (var i = 0; i < daten.pfosten_auswahl.length; i++) {
          var r = daten.pfosten_auswahl[i];
          if ((r.material === modell.material || r.material === '*') && r.befestigung === befestigung.id &&
              (r.system || 'ipf') === system && hoehe <= r.bis_hoehe_cm) return r;
        }
        return null;
      };
      var kandidatenFuer = function (regel) {
        return daten.pfosten
          .filter(function (p) { return p.ausfuehrung === regel.ausfuehrung; })
          .sort(function (a, b) { return a.laenge_cm - b.laenge_cm; });
      };

      // Solllänge: aus der Tabelle je Befestigungsart; T-Eisenpfosten werden
      // nach Zaunhöhe gewählt (laenge_wie_hoehe).
      var laengenTabelle = (daten.pfostenlaenge_cm || {})[befestigung.id] || {};
      var sollFuer = function (regel) {
        return regel.laenge_wie_hoehe ? hoehe : (laengenTabelle[String(hoehe)] || hoehe);
      };

      // Zusätzliche Länge je Pfosten durch Hang-Stufen.
      // Bausatz = Schrägmontage ohne Stufen -> überall Solllänge.
      var extras = bausatz ? [] : pfostenExtras(segmente, aufteilung, !!eingaben.geschlossen);
      var mitStufe = 0, maxExtra = 0;
      for (var pi = 0; pi < pfostenAnzahl; pi++) {
        var extraVor = Math.ceil(extras[pi] || 0);
        if (extraVor > 0) mitStufe++;
        if (extraVor > maxExtra) maxExtra = extraVor;
      }

      // Systemwahl mit automatischem Wechsel auf reguläre Pfosten + Halter:
      // (a) für die Zaunhöhe gibt es das gewünschte System nicht, oder
      // (b) dessen Längen reichen für die Hang-Stufen nicht, die der regulären
      //     Pfosten aber schon. Kunden sollen das nicht aus einem Warntext
      //     herauslesen müssen – der Planer wechselt selbst.
      var pfostenSystemAuto = null;
      pfostenRegel = findePfostenRegel(systemWunsch);
      if (!pfostenRegel && systemWunsch !== 'halter') {
        pfostenRegel = findePfostenRegel('halter');
        if (pfostenRegel) pfostenSystemAuto = 'hoehe';
      }
      if (!pfostenRegel) {
        return { ok: false, fehler: ['Für ' + modell.material + ' / ' + befestigung.name + ' ist noch kein Pfosten hinterlegt.'] };
      }
      var kandidaten = kandidatenFuer(pfostenRegel);
      if (!kandidaten.length) {
        return { ok: false, fehler: ['Für die Pfosten-Ausführung ' + pfostenRegel.ausfuehrung + ' sind keine Artikel gemappt.'] };
      }
      if ((pfostenRegel.system || 'ipf') !== 'halter' &&
          sollFuer(pfostenRegel) + maxExtra > kandidaten[kandidaten.length - 1].laenge_cm) {
        var bisherMax = kandidaten[kandidaten.length - 1].laenge_cm;
        var halterRegel = findePfostenRegel('halter');
        var halterKandidaten = halterRegel ? kandidatenFuer(halterRegel) : [];
        if (halterKandidaten.length &&
            halterKandidaten[halterKandidaten.length - 1].laenge_cm >= sollFuer(halterRegel) + maxExtra) {
          pfostenRegel = halterRegel;
          kandidaten = halterKandidaten;
          pfostenSystemAuto = 'stufen';
          hinweise.push('Wegen der Hang-Stufen braucht es längere Pfosten, als es sie im gewählten Pfostensystem gibt (bis ' +
            bisherMax + ' cm). Wir haben deshalb reguläre Vierkantpfosten mit Pfostenhaltern eingeplant.');
        }
      }
      if (pfostenSystemAuto === 'hoehe') {
        hinweise.push('Für diese Zaunhöhe gibt es das gewählte Pfostensystem nicht – ' +
          'wir haben reguläre Vierkantpfosten mit Pfostenhaltern eingeplant.');
      }
      var sollLaenge = sollFuer(pfostenRegel);
      var maxBenoetigt = sollLaenge + maxExtra;
      var maxVerfuegbar = kandidaten[kandidaten.length - 1].laenge_cm;

      var gruppen = {};
      var pfostenDetails = []; // je Pfosten in Verlaufsreihenfolge (u. a. für die Aufbauanleitung)
      for (var pj = 0; pj < pfostenAnzahl; pj++) {
        var extraHier = Math.ceil(extras[pj] || 0);
        var benoetigt = sollLaenge + extraHier;
        var artikel = null;
        for (var j = 0; j < kandidaten.length; j++) {
          if (kandidaten[j].laenge_cm >= benoetigt) { artikel = kandidaten[j]; break; }
        }
        if (!artikel) { artikel = kandidaten[kandidaten.length - 1]; pfostenZuLang++; }
        if (!gruppen[artikel.sku]) gruppen[artikel.sku] = { artikel: artikel, menge: 0 };
        gruppen[artikel.sku].menge++;
        pfostenDetails.push({ laengeCm: artikel.laenge_cm, extraCm: extraHier });
      }
      var gruppenListe = Object.keys(gruppen).map(function (sku) { return gruppen[sku]; })
        .sort(function (a, b) { return a.artikel.laenge_cm - b.artikel.laenge_cm; });
      gruppenListe.forEach(function (g) {
        positionen.push({
          typ: 'pfosten',
          sku: g.artikel.sku,
          bezeichnung: pfostenRegel.anzeige + ', ' + g.artikel.laenge_cm + ' cm',
          handle: g.artikel.handle,
          optionen: optionenListe(g.artikel),
          menge: g.menge,
          einzelpreis: g.artikel.preis != null ? Number(g.artikel.preis) : null
        });
      });

      if (mitStufe > 0 && !pfostenZuLang) {
        hinweise.push('Hang-Stufen: ' + mitStufe + ' Pfosten an den Übergängen sind länger kalkuliert ' +
          '(Standardlänge + Stufenhöhe) – siehe Stückliste.');
      }
      if (pfostenZuLang > 0) {
        hinweise.push('Achtung: An ' + pfostenZuLang + ' Übergängen bräuchte der Pfosten rechnerisch bis zu ' + maxBenoetigt +
          ' cm – länger als die längste verfügbare Variante (' + maxVerfuegbar + ' cm). ' +
          'Wähle die Lieferart „Einzelteile (Bausatz)“ – bei der Schrägmontage sind alle Pfosten gleich lang – ' +
          'oder sende uns die Konfiguration als Anfrage.');
      } else if (!mitStufe && gruppenListe.length === 1 && gruppenListe[0].artikel.laenge_cm !== sollLaenge) {
        hinweise.push('Pfostenlänge ' + sollLaenge + ' cm nicht verfügbar – nächstlängere Variante (' +
          gruppenListe[0].artikel.laenge_cm + ' cm) gewählt.');
      }

      // Pfostenhalter: reguläre Pfosten (ohne Nut) brauchen je Pfosten einen
      // Halter – H-Steg beim Einbetonieren, Dübelplatten-Halter beim Aufdübeln.
      if (pfostenRegel.halter_ausfuehrung) {
        if (istEntfernt('halter')) {
          hinweise.push('Pfostenhalter abgewählt – du brauchst dann selbst je Pfosten einen passenden Halter (hier: ' +
            pfostenAnzahl + ' Stück, ' + (pfostenRegel.halter_anzeige || 'Pfostenhalter') + ').');
        } else {
          var halterArtikel = (daten.halter || [])
            .filter(function (h) { return h.ausfuehrung === pfostenRegel.halter_ausfuehrung; })
            .sort(function (a, b) { return (a.laenge_cm || 0) - (b.laenge_cm || 0); })[0];
          if (halterArtikel) {
            positionen.push({
              typ: 'halter',
              sku: halterArtikel.sku,
              bezeichnung: pfostenRegel.halter_anzeige || 'Pfostenhalter',
              handle: halterArtikel.handle,
              optionen: optionenListe(halterArtikel),
              entfernbar: true,
              menge: pfostenAnzahl,
              einzelpreis: halterArtikel.preis != null ? Number(halterArtikel.preis) : null
            });
          } else {
            hinweise.push('Der Pfostenhalter "' + pfostenRegel.halter_ausfuehrung + '" ist noch nicht im Shop hinterlegt.');
          }
        }
      }
    }

    // --- Pfostenkappen (optional; nur für Holzpfosten, nicht für T-Eisenpfosten) ---
    var kappenMoeglich = !ohnePfosten && !(pfostenRegel && pfostenRegel.ohne_kappen);
    if (eingaben.mitKappen && kappenMoeglich) {
      var kappenRegel = (daten.kappen_auswahl || []).filter(function (k) {
        return k.querschnitt_cm === pfostenRegel.querschnitt_cm;
      })[0];
      var kappenArtikel = kappenRegel && daten.kappen.filter(function (k) { return k.sku === kappenRegel.sku; })[0];
      if (kappenArtikel) {
        positionen.push({
          typ: 'kappe',
          sku: kappenArtikel.sku,
          bezeichnung: kappenRegel.anzeige,
          handle: kappenArtikel.handle,
          optionen: optionenListe(kappenArtikel),
          entfernbar: true,
          menge: pfostenAnzahl,
          einzelpreis: kappenArtikel.preis != null ? Number(kappenArtikel.preis) : null
        });
      } else {
        hinweise.push('Für diesen Pfostenquerschnitt ist noch keine Pfostenkappe hinterlegt.');
      }
    }

    // --- Zaunwinkel: befestigen die Riegel an den Holzpfosten ---
    // (T-Eisenpfosten: Felder werden direkt am T-Eisen verschraubt, keine Winkel)
    var winkelRegel = daten.winkel_regel;
    if (winkelRegel && !ohnePfosten && !(pfostenRegel && pfostenRegel.ohne_winkel)) {
      var winkelMenge = aufteilung.anzahl * (winkelRegel.pro_feld || 4);
      if (istEntfernt('winkel')) {
        hinweise.push('Zaunwinkel abgewählt – für die Befestigung der Riegel an den Pfosten brauchst du dann selbst ' +
          winkelMenge + ' Stück (' + (winkelRegel.pro_feld || 4) + ' je Feld).');
      } else {
        var winkelArtikel = (daten.winkel || []).filter(function (w) { return w.sku === winkelRegel.sku; })[0];
        if (winkelArtikel) {
          positionen.push({
            typ: 'winkel',
            sku: winkelArtikel.sku,
            bezeichnung: winkelRegel.anzeige || 'Zaunwinkel',
            handle: winkelArtikel.handle,
            optionen: optionenListe(winkelArtikel),
            entfernbar: true,
            menge: winkelMenge,
            einzelpreis: winkelArtikel.preis != null ? Number(winkelArtikel.preis) : null
          });
        } else {
          hinweise.push('Zaunwinkel für die Riegelbefestigung sind noch nicht im Shop hinterlegt.');
        }
      }
    }

    // --- Summen & Hinweise ---
    var summe = 0;
    var preiseVollstaendig = true;
    positionen.forEach(function (p) {
      if (p.einzelpreis == null) {
        preiseVollstaendig = false;
        p.gesamtpreis = null;
        hinweise.push('Für "' + p.bezeichnung + '" liegt noch kein Shop-Preis vor.');
      } else {
        p.gesamtpreis = rund2(p.einzelpreis * p.menge);
        summe += p.gesamtpreis;
      }
    });

    if (aufteilung.ueberstandCm > 0) {
      hinweise.unshift('Die Feldkombination ergibt ' + meterText(aufteilung.gesamtCm) + ' – das sind ' +
        aufteilung.ueberstandCm + ' cm mehr als gewünscht' +
        (segmente.length > 1 ? ' (über alle Abschnitte)' : '') +
        '. Das letzte Feld je Abschnitt kann vor Ort gekürzt werden.');
    }

    // --- Gefälle: Stufenhöhe zwischen benachbarten Feldern abschätzen ---
    var maxStufeCm = 0;
    segmente.forEach(function (s, i) {
      if (s.laenge_cm <= 0) return;
      var gefaelle = Math.abs(s.hoehen_delta_cm) / s.laenge_cm;
      aufteilung.proSegment[i].felder.forEach(function (g) {
        var stufe = g.laengeCm * gefaelle;
        if (stufe > maxStufeCm) maxStufeCm = stufe;
      });
    });
    maxStufeCm = Math.round(maxStufeCm);
    // Bausatz am Hang = Schrägmontage: Riegel folgen dem Gelände, Latten stehen
    // senkrecht ("im Wasser") – keine Stufen, keine Lücken.
    var schraegMontage = bausatz && maxStufeCm > 0;
    if (maxStufeCm > 0) {
      if (schraegMontage) {
        hinweise.push('Bausatz am Hang: Die Riegel folgen dem Gelände und die Latten werden senkrecht („im Wasser“) montiert – ' +
          'der Zaun läuft ohne Stufen und ohne Lücken durch. So zeigt es auch die 3D-Ansicht.');
      } else {
        hinweise.push('Gelände mit Gefälle: Fertig montierte Felder bleiben waagerecht und folgen dem Hang in Stufen (größte Stufe ca. ' +
          maxStufeCm + ' cm).');
        if (maxStufeCm >= (grenzen.stufen_hinweis_ab_cm || 30)) {
          hinweise.push('Achtung, bei so hohen Stufen entstehen unter dem Zaun große Lücken. Unsere Empfehlung am Hang: ' +
            'Standard-Längen mit Lieferart „Einzelteile (Bausatz)“ – dann folgen die Riegel dem Gelände und die Latten werden ' +
            'senkrecht („im Wasser“) montiert, ganz ohne Stufen. Wir beraten dich gern.');
        }
      }
    }
    if (ohnePfosten) {
      hinweise.push('Pfosten und Kappen sind nicht enthalten – für diese Aufteilung brauchst du ' +
        pfostenAnzahl + ' vorhandene Pfosten.');
      hinweise.push('Bitte prüfe die lichten Weiten zwischen deinen Pfosten: Die Felder sind ' +
        aufteilung.felder.map(function (g) { return g.anzahl + ' × ' + g.laengeCm + ' cm'; }).join(' und ') +
        ' lang' + (massModus ? '.' : '; das letzte Feld kann vor Ort gekürzt werden.'));
    } else if (pfostenRegel.zwischen_feldern === false) {
      // T-Eisenpfosten sitzen hinter den direkt gestoßenen Feldern
      hinweise.push('T-Eisenpfosten sitzen hinter den Feldern, die Felder werden direkt gestoßen – ' +
        'die Zaunlänge bleibt bei ' + meterText(aufteilung.gesamtCm) + '.');
    } else {
      var gesamtMitPfostenCm = aufteilung.gesamtCm + pfostenAnzahl * pfostenRegel.querschnitt_cm;
      hinweise.push('Tatsächliche Zaunlänge inkl. Pfosten: ca. ' + meterText(Math.round(gesamtMitPfostenCm)) + '.');
    }

    return {
      ok: true,
      positionen: positionen,
      hinweise: hinweise,
      annahmen: daten.annahmen || [],
      aufteilung: aufteilung,
      segmente: segmente,
      pfostenAnzahl: pfostenAnzahl,
      ohnePfosten: ohnePfosten,
      pfostensystem: ohnePfosten ? null : (pfostenRegel.system || 'ipf'),
      pfostenSystemAuto: ohnePfosten ? null : pfostenSystemAuto,
      pfostenDetails: ohnePfosten ? null : pfostenDetails,
      pfostenZuLang: pfostenZuLang,
      kappenMoeglich: kappenMoeglich,
      lieferart: lieferart.id,
      feldmodus: massModus ? 'mass' : 'standard',
      massVerfuegbar: massVerfuegbar,
      massMaxCm: massMaxCm,
      hoeheStandard: hoeheStandard,
      schraegMontage: schraegMontage,
      maxGefaellePct: Math.round(maxGefaellePct * 10) / 10,
      maxStufeCm: maxStufeCm,
      laengeCm: laengeCm,
      summe: rund2(summe),
      preiseVollstaendig: preiseVollstaendig
    };
  }

  /* Kurzbeschreibung der Konfiguration, z. B. für Line-Item-Properties. */
  function zusammenfassung(eingaben, daten) {
    var modell = findeModell(daten, eingaben.modellId);
    var befestigung = findeBefestigung(daten, eingaben.befestigungId);
    var segmente = segmenteAusEingaben(eingaben);
    var laengeCm = 0;
    segmente.forEach(function (s) { laengeCm += s.laenge_cm; });
    var teile = [];
    if (modell) teile.push(modell.name);
    if (laengeCm > 0) {
      teile.push(meterText(laengeCm) + (eingaben.geschlossen
        ? ' rundum geschlossen'
        : segmente.length > 1 ? ' in ' + segmente.length + ' Abschnitten' : ''));
    }
    if (eingaben.hoehe_cm) teile.push(eingaben.hoehe_cm + ' cm hoch');
    if (befestigung) teile.push(befestigung.name);
    if (eingaben.feldmodusId === 'mass') teile.push('Felder nach Maß');
    if (eingaben.lieferartId === 'bausatz') teile.push('als Bausatz');
    return teile.join(' · ');
  }

  return {
    feldAufteilung: feldAufteilung,
    feldAufteilungSegmente: feldAufteilungSegmente,
    segmenteAusEingaben: segmenteAusEingaben,
    berechneStueckliste: berechneStueckliste,
    zusammenfassung: zusammenfassung
  };
}));
